<template>
  <div
    class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gray-900 text-white scrollbar-thin"
    id="style-1"
  >
    <div class="p-4 max-w-7xl mx-auto">
      <!-- Notification -->
      <transition name="fade">
        <div
          v-if="notification"
          class="fixed top-4 right-4 p-3 rounded-lg shadow-xl z-50 max-w-sm bg-gray-800 border animate-fadeIn"
          :class="
            notification.type === 'success'
              ? 'text-green-300 border-green-600'
              : 'text-red-300 border-red-600'
          "
        >
          <div class="flex items-center space-x-3">
            <i
              :class="
                notification.type === 'success'
                  ? 'fas fa-check-circle'
                  : 'fas fa-exclamation-triangle'
              "
              class="text-sm"
            ></i>
            <span class="text-sm">{{ notification.message }}</span>
            <button
              @click="notification = null"
              class="ml-auto text-sm text-gray-300 hover:text-indigo-300 transition-all duration-200 group"
            >
              <i class="fas fa-times text-sm group-hover:scale-110"></i>
            </button>
          </div>
        </div>
      </transition>

      <!-- Header -->
      <header class="flex items-center justify-between mb-6 animate-fadeIn">
        <h1 class="text-2xl font-semibold text-indigo-300">จัดการคำขอเติมเงิน</h1>
        <div class="flex items-center gap-2">
          <button
            @click="fetchRequests"
            class="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-500 px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-indigo-700 text-sm group"
          >
            <i class="fas fa-sync-alt text-sm group-hover:scale-110"></i>
            <span>รีเฟรช</span>
          </button>
        </div>
      </header>

      <!-- UID Input Section -->
      <div class="mb-6 bg-gray-800 bg-opacity-80 p-4 rounded-md border border-gray-700 animate-fadeIn">
        <h2 class="text-lg font-semibold text-indigo-300 mb-3">ค้นหาประวัติการเติมเงิน</h2>
        <div class="flex items-center space-x-3">
          <input
            v-model="searchUid"
            type="text"
            placeholder="กรอก UID เพื่อค้นหา"
            class="bg-gray-900 text-indigo-200 border border-gray-600 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
          />
          <button
            @click="fetchTotalTopupAndCharacterName(searchUid)"
            :disabled="!searchUid"
            class="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-500 px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-indigo-700 text-sm group"
            :class="{ 'opacity-60 cursor-not-allowed': !searchUid }"
          >
            <i class="fas fa-search text-sm group-hover:scale-110"></i>
            <span>ค้นหา</span>
          </button>
        </div>
      </div>

      <!-- Total Top-Up and Character Name Section -->
      <div v-if="totalTopup !== null" class="mb-6 bg-gray-800 bg-opacity-80 p-4 rounded-md border border-gray-700 animate-fadeIn">
        <h2 class="text-lg font-semibold text-indigo-300 mb-3">ข้อมูลยอดเติมเงิน</h2>
        <div class="text-indigo-200">
          <p><strong>ชื่อตัวละคร:</strong> {{ characterName }}</p>
          <p><strong>ยอดเติมเงินรวม:</strong> {{ totalTopup }} ฿</p>
        </div>
        <button
          @click="clearTotalTopup"
          class="mt-4 inline-flex items-center space-x-2 bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm group"
        >
          <i class="fas fa-times text-sm group-hover:scale-110"></i>
          <span>ล้างข้อมูล</span>
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-auto scrollbar-thin">
        <!-- Loading State -->
        <div v-if="isLoading" class="flex flex-col items-center py-20 animate-fadeIn">
          <i class="fas fa-spinner text-4xl text-indigo-400 animate-spin-pulse"></i>
          <p class="text-indigo-200 text-sm mt-4">กำลังโหลด...</p>
        </div>

        <!-- Error State -->
        <div
          v-else-if="error"
          class="text-center text-red-300 py-20 bg-gray-900 bg-opacity-80 p-3 rounded-md border border-red-600 animate-fadeIn"
        >
          <p class="text-sm">{{ error }}</p>
          <button
            @click="fetchRequests"
            class="mt-4 inline-flex items-center space-x-2 bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm group"
          >
            <i class="fas fa-redo text-sm group-hover:scale-110"></i>
            <span>ลองใหม่</span>
          </button>
        </div>

        <!-- Requests List -->
        <div v-else class="animate-fadeIn">
          <div
            v-if="topupRequests.length === 0"
            class="text-indigo-200 text-center py-10 text-sm bg-gray-900 bg-opacity-80 border border-gray-800 rounded-md"
          >
            ไม่มีคำขอเติมเงิน
          </div>

          <div v-else>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div
                v-for="request in topupRequests"
                :key="request.id"
                class="bg-gray-900 bg-opacity-80 rounded-md p-4 shadow hover:bg-gray-800 transition-all duration-200 border border-gray-800"
              >
                <div class="flex items-center justify-between mb-4">
                  <span class="font-semibold text-sm text-indigo-300 truncate">{{ request.uid }}</span>
                  <div class="flex items-center space-x-2">
                    <button
                      @click.stop="fetchTotalTopupAndCharacterName(request.uid)"
                      class="text-indigo-300 hover:text-indigo-400 transition-all duration-200 group"
                      title="ดูยอดเติมเงินรวมและชื่อตัวละคร"
                    >
                      <i class="fas fa-eye text-sm group-hover:scale-110"></i>
                    </button>

                    <!-- Delete button on card -->
                    <button
                      @click.stop="askDelete(request)"
                      class="text-red-400 hover:text-red-300 transition-all duration-200 group"
                      title="ลบคำขอ"
                    >
                      <i class="fas fa-trash text-sm group-hover:scale-110"></i>
                    </button>

                    <span
                      class="px-3 py-1 rounded-full text-xs font-semibold"
                      :class="statusClass(request.status)"
                    >
                      {{ statusText(request.status) }}
                    </span>
                  </div>
                </div>

                <button
                  class="w-full text-left"
                  @click="openModal(request)"
                  title="เปิดรายละเอียด"
                >
                  <ul class="space-y-2 text-sm text-indigo-200">
                    <li>
                      <i class="fas fa-money-bill-wave mr-2 text-indigo-300"></i>จำนวน:
                      {{ request.amount }} ฿
                    </li>
                    <li>
                      <i class="fas fa-money-bill mr-2 text-green-500"></i>แคช:
                      {{ request.cash }}
                    </li>
                    <li>
                      <i class="fas fa-calendar-alt mr-2 text-indigo-300"></i>โอน:
                      {{ formatDate(request.transferTime) }}
                    </li>
                    <li>
                      <i class="fas fa-clock mr-2 text-indigo-300"></i>ยื่น:
                      {{ formatDate(request.createdAt) }}
                    </li>
                  </ul>
                </button>
              </div>
            </div>

            <!-- Load More -->
            <div class="flex items-center justify-center mt-6">
              <button
                v-if="hasMore && !isLoadingMore"
                @click="loadMore"
                class="inline-flex items-center gap-2 bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm"
              >
                <i class="fas fa-plus text-sm"></i>
                <span>โหลดเพิ่ม</span>
              </button>

              <div v-else-if="isLoadingMore" class="flex items-center gap-2 text-indigo-200">
                <i class="fas fa-spinner animate-spin-pulse text-sm"></i>
                <span class="text-sm">กำลังโหลดเพิ่มเติม...</span>
              </div>

              <div v-else class="text-xs text-gray-400">
                แสดงครบทั้งหมดแล้ว
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal: Request details -->
      <transition name="modal">
        <div
          v-if="selectedRequest"
          class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50 p-4 animate-modalFade"
          @click.self="closeModal"
        >
          <div
            class="bg-gray-800 rounded-lg w-full max-w-md p-6 shadow-lg border border-gray-600 animate-scaleIn"
          >
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-lg font-semibold text-indigo-300">ตรวจสอบคำขอเติมเงิน</h2>
              <button
                @click="closeModal"
                class="text-gray-300 hover:text-indigo-300 group"
              >
                <i class="fas fa-times text-sm group-hover:scale-110 transition-transform duration-200"></i>
              </button>
            </div>
            <div class="space-y-3 text-indigo-200 text-sm mb-6">
              <p>
                <i class="fas fa-user mr-2 text-indigo-300"></i>ผู้ใช้:
                {{ selectedRequest.uid }}
              </p>
              <p>
                <i class="fas fa-money-bill-wave mr-2 text-indigo-300"></i>จำนวน:
                {{ selectedRequest.amount }} ฿
              </p>
              <p>
                <i class="fas fa-money-bill mr-2 text-green-500"></i>แคช:
                {{ selectedRequest.cash }}
              </p>
              <p>
                <i class="fas fa-calendar-alt mr-2 text-indigo-300"></i>โอน:
                {{ formatDate(selectedRequest.transferTime) }}
              </p>
              <a
                :href="selectedRequest.receiptUrl"
                target="_blank"
                class="inline-flex items-center text-indigo-400 hover:text-indigo-300 transition-all duration-200"
              >
                <i class="fas fa-file-alt mr-2 text-indigo-300"></i>ดูใบเสร็จ
              </a>
            </div>

            <div class="flex space-x-3">
              <button
                @click="processRequest('success')"
                :disabled="isProcessing"
                class="flex-1 py-2 rounded-md font-semibold transition-all duration-200 text-sm border border-green-700"
                :class="isProcessing && actionType === 'success'
                    ? 'bg-green-600 opacity-60 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-500'"
              >
                <span v-if="!(isProcessing && actionType === 'success')">อนุมัติ</span>
                <i v-else class="fas fa-spinner animate-spin-pulse text-sm"></i>
              </button>
              <button
                @click="processRequest('failed')"
                :disabled="isProcessing"
                class="flex-1 py-2 rounded-md font-semibold transition-all duration-200 text-sm border border-red-700"
                :class="isProcessing && actionType === 'failed'
                    ? 'bg-red-600 opacity-60 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-500'"
              >
                <span v-if="!(isProcessing && actionType === 'failed')">ปฏิเสธ</span>
                <i v-else class="fas fa-spinner animate-spin-pulse text-sm"></i>
              </button>
            </div>

            <!-- Delete from inside modal -->
            <button
              @click="askDelete(selectedRequest)"
              :disabled="isDeleting"
              class="mt-3 w-full py-2 rounded-md font-semibold transition-all duration-200 text-sm border border-gray-600 bg-gray-700 hover:bg-gray-600"
            >
              <i class="fas fa-trash mr-2"></i>ลบคำขอ
            </button>

            <button
              @click="closeModal"
              class="mt-3 w-full py-2 bg-gray-600 hover:bg-gray-500 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm group"
            >
              <i class="fas fa-times mr-1 text-sm group-hover:scale-110"></i>
              ยกเลิก
            </button>
          </div>
        </div>
      </transition>

      <!-- Confirm Delete Modal -->
      <transition name="modal">
        <div
          v-if="deleteTarget"
          class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-50 p-4 animate-modalFade"
          @click.self="cancelDelete"
        >
          <div class="bg-gray-800 rounded-lg w-full max-w-sm p-6 shadow-lg border border-gray-600 animate-scaleIn">
            <div class="flex items-center mb-4">
              <i class="fas fa-exclamation-triangle text-red-400 mr-2"></i>
              <h3 class="text-base font-semibold text-red-300">ยืนยันการลบ</h3>
            </div>
            <p class="text-sm text-indigo-200 mb-4">
              ต้องการลบคำขอของผู้ใช้ <span class="font-semibold">{{ deleteTarget.uid }}</span>
              จำนวน <span class="font-semibold">{{ deleteTarget.amount }} ฿</span> จริงหรือไม่?
              การลบไม่สามารถย้อนกลับได้
            </p>
            <div class="flex gap-3">
              <button
                @click="performDelete"
                :disabled="isDeleting"
                class="flex-1 py-2 rounded-md font-semibold transition-all duration-200 text-sm border border-red-700"
                :class="isDeleting ? 'bg-red-600 opacity-60 cursor-not-allowed' : 'bg-red-600 hover:bg-red-500'"
              >
                <span v-if="!isDeleting"><i class="fas fa-trash mr-2"></i>ลบ</span>
                <i v-else class="fas fa-spinner animate-spin-pulse text-sm"></i>
              </button>
              <button
                @click="cancelDelete"
                :disabled="isDeleting"
                class="flex-1 py-2 rounded-md font-semibold transition-all duration-200 text-sm border border-gray-600 bg-gray-700 hover:bg-gray-600"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import firebase from 'firebase/app';
import { projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';

export default {
  name: 'TopupAdmin',
  setup() {
    const { user } = getUser();
    const playerStore = usePlayerStore();

    // Core states
    const isLoading = ref(false);
    const error = ref('');
    const notification = ref(null);
    const topupRequests = ref([]);
    const selectedRequest = ref(null);
    const isProcessing = ref(false);
    const actionType = ref('');

    // Search & display helpers
    const totalTopup = ref(null);
    const characterName = ref('');
    const searchUid = ref('');

    // Realtime unsubscribe
    let unsubscribe = null;

    // Pagination states
    const pageSize = 20;
    const lastVisible = ref(null);
    const isLoadingMore = ref(false);
    const hasMore = ref(true);

    // Delete states
    const deleteTarget = ref(null);
    const isDeleting = ref(false);

    const notify = (type, msg) => {
      notification.value = { type, message: msg };
      setTimeout(() => (notification.value = null), 3000);
    };

    // ===== Admin check & initial fetch =====
    const checkAdmin = async () => {
      if (!user.value) {
        error.value = 'กรุณาล็อกอิน';
        return;
      }
      try {
        if (!playerStore.userData?.admin) {
          error.value = 'สิทธิ์ไม่เพียงพอ';
        } else {
          await fetchRequests(); // initial
        }
      } catch (err) {
        error.value = `เกิดข้อผิดพลาด: ${err.message}`;
        notify('error', error.value);
      }
    };

    // ===== Fetch first page with realtime updates on the first page =====
    const fetchRequests = async () => {
      isLoading.value = true;
      error.value = '';
      topupRequests.value = [];
      lastVisible.value = null;
      hasMore.value = true;

      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }

      try {
        const baseQuery = projectFirestore
          .collection('topupRequests')
          .orderBy('createdAt', 'desc')
          .limit(pageSize);

        unsubscribe = baseQuery.onSnapshot(
          (snap) => {
            const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            topupRequests.value = items;
            lastVisible.value = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
            hasMore.value = snap.size === pageSize;
            isLoading.value = false;
          },
          (err) => {
            error.value = `โหลดไม่สำเร็จ: ${err.message}`;
            notify('error', error.value);
            isLoading.value = false;
          }
        );
      } catch (err) {
        error.value = `เกิดข้อผิดพลาด: ${err.message}`;
        notify('error', error.value);
        isLoading.value = false;
      }
    };

    // ===== Load more pages (non-realtime append) =====
    const loadMore = async () => {
      if (isLoadingMore.value || !hasMore.value || !lastVisible.value) return;
      isLoadingMore.value = true;

      try {
        const nextSnap = await projectFirestore
          .collection('topupRequests')
          .orderBy('createdAt', 'desc')
          .startAfter(lastVisible.value)
          .limit(pageSize)
          .get();

        const newItems = nextSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Deduplicate in case of overlap
        const existingIds = new Set(topupRequests.value.map((i) => i.id));
        const merged = [...topupRequests.value];
        newItems.forEach((it) => {
          if (!existingIds.has(it.id)) merged.push(it);
        });

        topupRequests.value = merged;
        lastVisible.value = nextSnap.docs.length ? nextSnap.docs[nextSnap.docs.length - 1] : lastVisible.value;
        hasMore.value = nextSnap.size === pageSize;
      } catch (err) {
        notify('error', `โหลดเพิ่มเติมไม่สำเร็จ: ${err.message}`);
      } finally {
        isLoadingMore.value = false;
      }
    };

    // ===== Modal & processing =====
    const openModal = (req) => (selectedRequest.value = req);
    const closeModal = () => {
      selectedRequest.value = null;
      isProcessing.value = false;
      actionType.value = '';
    };

    const processRequest = async (status) => {
      if (!selectedRequest.value) return;
      isProcessing.value = true;
      actionType.value = status;
      const callerUid = user.value.uid;

      try {
        if (!playerStore.userData?.admin) {
          throw new Error('คุณไม่มีสิทธิ์อนุมัติคำขอ');
        }

        if (status === 'success') {
          await projectFirestore.runTransaction(async (transaction) => {
            const requestRef = projectFirestore.collection('topupRequests').doc(selectedRequest.value.id);
            const statsRef = projectFirestore.collection('stats').doc('global');

            const requestDoc = await transaction.get(requestRef);
            if (!requestDoc.exists) throw new Error('ไม่พบคำขอ');
            if (requestDoc.data().status !== 'pending') throw new Error('คำขอนี้ถูกดำเนินการแล้ว');

            const statsDoc = await transaction.get(statsRef);
            if (!statsDoc.exists) {
              transaction.set(statsRef, {
                totalTopupRequestsApproved: 0,
                totalAmountToppedUp: 0,
                totalCashReceivedFromTopups: 0,
              });
            }

            const uid = requestDoc.data().uid;
            const cash = requestDoc.data().cash;
            const amount = requestDoc.data().amount;

            if (!Number.isFinite(cash) || cash <= 0) throw new Error(`ข้อมูล cash ไม่ถูกต้อง: ${cash}`);
            if (!Number.isFinite(amount) || amount <= 0) throw new Error(`ข้อมูล amount ไม่ถูกต้อง: ${amount}`);

            const userRef = projectFirestore.collection('users').doc(uid);
            const userDoc = await transaction.get(userRef);
            if (!userDoc.exists) throw new Error('ไม่พบผู้ใช้');

            const currentCash = userDoc.data().cash || 0;

            transaction.update(requestRef, { status: 'success' });
            transaction.update(userRef, { cash: currentCash + cash });
            transaction.update(statsRef, {
              totalTopupRequestsApproved: firebase.firestore.FieldValue.increment(1),
              totalAmountToppedUp: firebase.firestore.FieldValue.increment(amount),
              totalCashReceivedFromTopups: firebase.firestore.FieldValue.increment(cash),
            });

            const logRef = projectFirestore.collection('logs').doc();
            transaction.set(logRef, {
              uid,
              action: 'approve_topup',
              timestamp: firebase.firestore.FieldValue.serverTimestamp(),
              details: {
                requestId: selectedRequest.value.id,
                adminUid: callerUid,
                amount,
                cash,
                before: currentCash,
                after: currentCash + cash,
              },
              displayMessage: `แอดมิน ${callerUid} อนุมัติเติมเงินให้ ${uid} (${amount} บาท, cash ${cash})`,
              success: true,
              error: null,
              functionName: 'approveTopupRequest',
            });
          });
        } else {
          const requestRef = projectFirestore.collection('topupRequests').doc(selectedRequest.value.id);
          const requestDoc = await requestRef.get();
          if (!requestDoc.exists) throw new Error('ไม่พบคำขอ');
          if (requestDoc.data().status !== 'pending') throw new Error('คำขอนี้ถูกดำเนินการแล้ว');

          await requestRef.update({ status: 'failed' });
          await projectFirestore.collection('logs').doc().set({
            uid: selectedRequest.value.uid,
            action: 'reject_topup',
            timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            details: { requestId: selectedRequest.value.id, adminUid: callerUid },
            displayMessage: `แอดมิน ${callerUid} ปฏิเสธเติมเงินของ ${selectedRequest.value.uid}`,
            success: true,
            error: null,
            functionName: 'rejectTopupRequest',
          });
        }

        notify('success', status === 'success' ? 'อนุมัติสำเร็จ' : 'ปฏิเสธสำเร็จ');
        closeModal();
      } catch (err) {
        notify('error', `เกิดข้อผิดพลาด: ${err.message}`);
        await projectFirestore.collection('logs').doc().set({
          uid: selectedRequest.value ? selectedRequest.value.uid : 'unknown',
          action: status === 'success' ? 'approve_topup' : 'reject_topup',
          timestamp: firebase.firestore.FieldValue.serverTimestamp(),
          details: { adminUid: callerUid, errorCode: err.code || 'unknown', errorMessage: err.message || 'Unknown error' },
          displayMessage: `เกิดข้อผิดพลาดในการ${status === 'success' ? 'อนุมัติ' : 'ปฏิเสธ'}เติมเงิน: ${err.message}`,
          success: false,
          error: err.message || 'Unknown error',
          functionName: 'processTopupRequest',
        });
      } finally {
        isProcessing.value = false;
        actionType.value = '';
      }
    };

    // ===== Delete flow =====
    const askDelete = (req) => {
      deleteTarget.value = req;
    };
    const cancelDelete = () => {
      deleteTarget.value = null;
      isDeleting.value = false;
    };
    const performDelete = async () => {
      if (!deleteTarget.value) return;
      isDeleting.value = true;

      try {
        const callerUid = user.value?.uid || 'unknown';
        // Permission check (admin)
        if (!playerStore.userData?.admin) {
          throw new Error('คุณไม่มีสิทธิ์ลบคำขอนี้');
        }

        const requestId = deleteTarget.value.id;
        const uid = deleteTarget.value.uid;

        // Delete doc
        await projectFirestore.collection('topupRequests').doc(requestId).delete();

        // Optimistic remove from local list
        topupRequests.value = topupRequests.value.filter((r) => r.id !== requestId);

        // If modal open for the same request, close it
        if (selectedRequest.value && selectedRequest.value.id === requestId) {
          closeModal();
        }

        // Log
        await projectFirestore.collection('logs').doc().set({
          uid,
          action: 'delete_topup_request',
          timestamp: firebase.firestore.FieldValue.serverTimestamp(),
          details: {
            requestId,
            adminUid: callerUid,
          },
          displayMessage: `แอดมิน ${callerUid} ลบคำขอเติมเงินของ ${uid}`,
          success: true,
          error: null,
          functionName: 'deleteTopupRequest',
        });

        notify('success', 'ลบคำขอสำเร็จ');
      } catch (err) {
        notify('error', `ลบไม่สำเร็จ: ${err.message}`);
        await projectFirestore.collection('logs').doc().set({
          uid: deleteTarget.value ? deleteTarget.value.uid : 'unknown',
          action: 'delete_topup_request',
          timestamp: firebase.firestore.FieldValue.serverTimestamp(),
          details: { errorMessage: err.message || 'Unknown error' },
          displayMessage: `เกิดข้อผิดพลาดในการลบคำขอเติมเงิน: ${err.message}`,
          success: false,
          error: err.message || 'Unknown error',
          functionName: 'deleteTopupRequest',
        });
      } finally {
        isDeleting.value = false;
        deleteTarget.value = null;
      }
    };

    // ===== Utilities =====
    const fetchTotalTopupAndCharacterName = async (uid) => {
      if (!uid) {
        notify('error', 'กรุณากรอก UID');
        return;
      }
      try {
        isLoading.value = true;
        error.value = '';
        totalTopup.value = null;
        characterName.value = '';

        const snapshot = await projectFirestore
          .collection('topupRequests')
          .where('uid', '==', uid)
          .where('status', '==', 'success')
          .get();

        const total = snapshot.docs.reduce((sum, doc) => sum + (doc.data().amount || 0), 0);
        totalTopup.value = total;

        const charDoc = await projectFirestore.collection('characters').doc(uid).get();
        characterName.value = charDoc.exists ? (charDoc.data().characterName || 'Unknown') : 'ไม่พบตัวละคร';

        notify('success', 'ดึงข้อมูลยอดเติมเงินและชื่อตัวละครสำเร็จ');
      } catch (err) {
        error.value = `เกิดข้อผิดพลาดในการดึงข้อมูล: ${err.message}`;
        notify('error', error.value);
      } finally {
        isLoading.value = false;
      }
    };

    const clearTotalTopup = () => {
      totalTopup.value = null;
      characterName.value = '';
      searchUid.value = '';
    };

    const formatDate = (ts) => {
      if (!ts?.seconds) return 'ไม่ระบุ';
      return new Date(ts.seconds * 1000).toLocaleString('th-TH', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    };

    const statusText = (status) => {
      const statusMap = { pending: 'รอดำเนินการ', success: 'สำเร็จ', failed: 'ไม่สำเร็จ' };
      return statusMap[status] || status;
    };

    const statusClass = (status) => {
      const classMap = {
        pending: 'bg-yellow-600 text-white',
        success: 'bg-green-600 text-white',
        failed: 'bg-red-600 text-white',
      };
      return classMap[status] || 'bg-gray-600 text-white';
    };

    onMounted(() => checkAdmin());
    onUnmounted(() => { if (unsubscribe) unsubscribe(); });

    return {
      // states
      isLoading, error, notification,
      topupRequests, selectedRequest, isProcessing, actionType,
      totalTopup, characterName, searchUid,

      // pagination
      hasMore, isLoadingMore, loadMore,

      // delete
      deleteTarget, isDeleting, askDelete, cancelDelete, performDelete,

      // actions
      fetchRequests, openModal, closeModal, processRequest,
      fetchTotalTopupAndCharacterName, clearTotalTopup,

      // utils
      formatDate, statusText, statusClass,
    };
  },
};
</script>

<style scoped>
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fcondo.png?alt=media&token=198ebb1c-66e5-4bf0-aa79-32bff287aa09') no-repeat center center fixed,
    linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}
/* Custom Scrollbar */
.scrollbar-thin::-webkit-scrollbar { width: 5px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #2d3748; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #4a5568; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #6b7280; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #4a5568 #2d3748; }

/* Animations */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.3s ease-out; }

@keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.3s ease-out; }

@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }

@keyframes spin-pulse { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
.animate-spin-pulse { animation: spin-pulse 1s infinite linear; }

/* Modal Transition */
.modal-enter-active, .modal-leave-active { transition: opacity 0.3s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }

/* Fade Transition */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* Responsive Design */
@media (max-width: 639px) {
  .text-sm { font-size: 0.75rem; }
  .text-base { font-size: 0.875rem; }
  .p-4 { padding: 0.75rem; }
}
</style>
